/**
 * Runway Gen-4.5 官方图生视频接口
 *
 * POST /v1/image_to_video
 * GET  /v1/tasks/{id}
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var POLL_INTERVAL = 3000
var POLL_TIMEOUT = 10 * 60 * 1000

var CREATE_PATH = '/v1/image_to_video'
var TASKS_PATH = '/v1/tasks/'
var DEFAULT_BASE_URL = 'https://api.dev.runwayml.com'
var DEFAULT_VERSION = '2024-11-06'

function getRunwayConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_RUNWAY_BASE_URL || DEFAULT_BASE_URL).replace(/\/$/, ''),
    apiKey: (process.env.VUE_APP_OFFICIAL_RUNWAY_API_KEY || '').trim(),
    version: (process.env.VUE_APP_OFFICIAL_RUNWAY_VERSION || DEFAULT_VERSION).trim()
  }
}

function createClient() {
  var cfg = getRunwayConfig()
  if (!cfg.baseURL) throw new Error('未配置 VUE_APP_OFFICIAL_RUNWAY_BASE_URL')
  if (!cfg.apiKey) throw new Error('未配置 VUE_APP_OFFICIAL_RUNWAY_API_KEY')
  var instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: 120000,
    headers: {
      Authorization: 'Bearer ' + cfg.apiKey,
      'X-Runway-Version': cfg.version || DEFAULT_VERSION,
      'Content-Type': 'application/json'
    }
  })
  instance.interceptors.response.use(
    function (res) {
      return res.data
    },
    function (err) {
      var respData = err.response && err.response.data
      var msg =
        extractApiErrorMessage(respData) ||
        (respData && respData.failure) ||
        (respData && (respData.message || respData.messge)) ||
        err.message ||
        '请求失败'
      return Promise.reject(new Error(msg))
    }
  )
  return instance
}

function normalizeDuration(duration) {
  var d = parseInt(duration, 10)
  if (d !== 5 && d !== 10) d = 5
  return d
}

function normalizeSeed(seed) {
  var s = parseInt(seed, 10)
  if (isNaN(s)) return 0
  return s
}

function mapRatio(aspectRatio) {
  var key = String(aspectRatio || '16:9').trim()
  var table = {
    '16:9': '1280:720',
    '9:16': '720:1280',
    '1:1': '960:960',
    '4:3': '1104:832',
    '3:4': '832:1104',
    '21:9': '1584:672'
  }
  return table[key] || table['16:9']
}

export function submitVolcRunwayGen45Task(params) {
  var client = createClient()
  var prompt = (params.prompt || '').trim().slice(0, 2000)
  if (!prompt) return Promise.reject(new Error('prompt 必填'))
  if (!params.referenceImage) return Promise.reject(new Error('promptImage 必填（图生视频）'))

  var body = {
    promptText: prompt,
    promptImage: String(params.referenceImage),
    seed: normalizeSeed(params.seed),
    ratio: mapRatio(params.aspectRatio),
    duration: normalizeDuration(params.videoDuration),
    contentModeration: {
      publicFigureThreshold: 'low'
    },
    model: 'gen4.5'
  }

  return client.post(CREATE_PATH, body).then(function (data) {
    var taskId = data && data.id ? String(data.id) : ''
    if (!taskId) return Promise.reject(new Error('官方接口未返回任务 id'))
    return { taskId: taskId }
  })
}

function downloadRunwayOutputVideoBlob(videoUrl) {
  return fetch(String(videoUrl || ''), { mode: 'cors' })
    .then(function (res) {
      if (!res.ok) return Promise.reject(new Error('download video failed: HTTP ' + res.status))
      return res.blob()
    })
    .then(function (blob) {
      if (!blob || !blob.size) return Promise.reject(new Error('download video failed: empty blob'))
      return blob
    })
}

function parseTaskStatus(data) {
  var statusRaw = data && data.status ? String(data.status).toUpperCase() : ''
  if (statusRaw === 'SUCCEEDED') {
    var output = data && Array.isArray(data.output) ? data.output : []
    var firstUrl = output.length ? output[0] : ''
    if (!firstUrl) return { status: 'failed', url: null, error: '任务成功但未返回 output URL' }
    return { status: 'completed', url: String(firstUrl), error: null }
  }
  if (statusRaw === 'FAILED' || statusRaw === 'CANCELLED') {
    var failure = data && data.failure ? String(data.failure) : ''
    var failureCode = data && data.failureCode ? String(data.failureCode) : ''
    var msg = failure
      ? (failureCode ? (failure + ' (' + failureCode + ')') : failure)
      : (failureCode || '生成失败')
    return { status: 'failed', url: null, error: msg }
  }
  if (statusRaw === 'PENDING' || statusRaw === 'THROTTLED' || statusRaw === 'RUNNING') {
    return { status: 'processing', url: null, error: null }
  }
  return { status: 'failed', url: null, error: '任务状态未知：' + statusRaw }
}

export function pollVolcRunwayGen45Task(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var client = createClient()
  var path = TASKS_PATH + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client
      .get(path)
      .then(function (data) {
        if (stopped) return
        var parsed = parseTaskStatus(data)

        if (parsed.status === 'completed') {
          var urlDone = parsed.url
          downloadRunwayOutputVideoBlob(urlDone)
            .then(function (blob) {
              if (stopped) return
              if (typeof callback === 'function') {
                callback(null, { status: 'completed', url: urlDone, videoBlob: blob, error: null })
              }
              stop()
            })
            .catch(function () {
              if (stopped) return
              if (typeof callback === 'function') {
                callback(null, { status: 'completed', url: urlDone, videoBlob: null, error: null })
              }
              stop()
            })
          return
        }

        if (parsed.status === 'failed') {
          if (typeof callback === 'function') callback(null, parsed)
          stop()
          return
        }

        if (typeof callback === 'function') callback(null, parsed)
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
